/**
 * Wishlist Hero Price Formatter
 * Formats wishlist prices with Pix discount and proper BRL formatting
 */
window.WishlistHeroPriceFormatter = {
  _options: {},
  _processedPrices: new WeakMap(),

  /**
   * Parse English date string to Brazilian format
   * @param {string} dateString - Date string like "26 Dec 2025"
   * @returns {string} - Formatted string like "Adicionado em 26/12/2025"
   */
  formatDateBR(dateString) {
    if (!dateString) return '';

    const months = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };

    // Match pattern: "26 Dec 2025"
    const match = dateString.trim().match(/^(\d{1,2})\s+(\w{3})\s+(\d{4})$/);
    if (!match) return dateString;

    const [, day, monthAbbr, year] = match;
    const month = months[monthAbbr];

    if (!month) return dateString;

    return `Adicionado em ${day.padStart(2, '0')}/${month}/${year}`;
  },

  /**
   * Format a date element
   * @param {HTMLElement} element - The date element to format
   */
  formatDateElement(element) {
    const currentText = element.textContent.trim();

    // Skip if already formatted
    if (currentText.includes('Adicionado em')) return;

    const formattedDate = this.formatDateBR(currentText);
    if (formattedDate !== currentText) {
      element.textContent = formattedDate;
    }
  },

  /**
   * Parse a BRL price string to a number
   * @param {string} priceString - Price string like "R$7,635.29" or "R$ 7.635,29"
   * @returns {number} - Numeric value
   */
  parseBRLPrice(priceString) {
    if (!priceString) return 0;

    // Remove "no pix" label if present (already formatted)
    if (priceString.includes('no pix')) return 0;

    // Remove currency symbol and whitespace
    let cleaned = priceString.replace(/R\$\s*/gi, '').trim();

    // Detect format: Brazilian (1.234,56) vs US (1,234.56)
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    if (lastComma > lastDot && cleaned.length - lastComma === 3) {
      // Brazilian format: 7.635,29 -> remove dots, replace comma with dot
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma && cleaned.length - lastDot === 3) {
      // US format: 7,635.29 -> just remove commas
      cleaned = cleaned.replace(/,/g, '');
    } else {
      // Fallback: try removing all non-numeric except last decimal separator
      cleaned = cleaned.replace(/[^\d.,]/g, '');
      const match = cleaned.match(/^([\d.,]*?)([.,])(\d{2})$/);
      if (match) {
        cleaned = match[1].replace(/[.,]/g, '') + '.' + match[3];
      }
    }

    return parseFloat(cleaned) || 0;
  },

  /**
   * Format a number as BRL currency
   * @param {number} value - Numeric value
   * @returns {string} - Formatted string like "R$ 7.635,29"
   */
  formatBRL(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  },

  /**
   * Get the Pix discount multiplier from theme settings
   * @returns {number} - Multiplier (e.g., 0.9 for 10% off)
   */
  getPixMultiplier() {
    return parseFloat(window.Shop?.settings?.pixAVista) || 1;
  },

  /**
   * Calculate the Pix price
   * @param {number} originalPrice - Original price value
   * @returns {number} - Price with Pix discount applied
   */
  calculatePixPrice(originalPrice) {
    return originalPrice * this.getPixMultiplier();
  },

  /**
   * Format a price element with Pix discount
   * @param {HTMLElement} element - The price element to format
   */
  formatPriceElement(element) {
    const { showOriginal = false, pixLabel = 'no pix' } = this._options;

    // Get original price from data attribute (most reliable source)
    const originalPriceAttr = element.dataset.wlhItemPrice;
    const currentText = element.textContent.trim();

    // Skip if already contains our label
    if (currentText.includes(pixLabel)) return;

    // Parse the price - prefer data attribute, fallback to text content
    const priceText = originalPriceAttr || currentText;
    const originalPrice = this.parseBRLPrice(priceText);

    if (originalPrice === 0) return;

    const pixPrice = this.calculatePixPrice(originalPrice);
    const formattedPixPrice = this.formatBRL(pixPrice);

    // Store the expected formatted value to detect overwrites
    const expectedValue = showOriginal
      ? formattedPixPrice + ' ' + pixLabel
      : formattedPixPrice + ' ' + pixLabel;

    if (showOriginal) {
      const formattedOriginal = this.formatBRL(originalPrice);
      element.innerHTML = `
        <span class="wishlist-original-price line-through text-gray-500">${formattedOriginal}</span>
        <span class="wishlist-pix-price font-medium">${formattedPixPrice} ${pixLabel}</span>
      `;
    } else {
      element.textContent = `${formattedPixPrice} ${pixLabel}`;
    }

    // Track that we've processed this element
    this._processedPrices.set(element, expectedValue);
  },

  /**
   * Initialize and format all wishlist price elements
   * @param {Object} options - Formatting options
   */
  init(options = {}) {
    this._options = options;
    this.formatAll();
    this.observeChanges();
  },

  /**
   * Format all wishlist elements (prices and dates)
   */
  formatAll() {
    this.formatAllPrices();
    this.formatAllDates();
  },

  /**
   * Format all wishlist price elements on the page
   */
  formatAllPrices() {
    const priceElements = document.querySelectorAll(
      '.wishlist-hero-price-update [data-wlh-item-price], .wishlist-hero-price-update .money'
    );

    priceElements.forEach((element) => {
      this.formatPriceElement(element);
    });
  },

  /**
   * Format all wishlist date elements on the page
   */
  formatAllDates() {
    const dateElements = document.querySelectorAll(
      '.wishlist-hero-list-card-header-added-on'
    );

    dateElements.forEach((element) => {
      this.formatDateElement(element);
    });
  },

  /**
   * Observe DOM for new elements AND content changes
   */
  observeChanges() {
    if (this._observer) return;

    this._observer = new MutationObserver((mutations) => {
      let shouldProcess = false;

      mutations.forEach((mutation) => {
        // Handle new nodes
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for price containers
              if (
                node.matches?.('.wishlist-hero-price-update') ||
                node.closest?.('.wishlist-hero-price-update')
              ) {
                shouldProcess = true;
              }
              if (node.querySelector?.('.wishlist-hero-price-update')) {
                shouldProcess = true;
              }
              // Check for date elements
              if (
                node.matches?.('.wishlist-hero-list-card-header-added-on') ||
                node.querySelector?.('.wishlist-hero-list-card-header-added-on')
              ) {
                shouldProcess = true;
              }
            }
          });
        }

        // Handle text/content changes
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          const target = mutation.target;
          const priceContainer = target.nodeType === Node.TEXT_NODE
            ? target.parentElement
            : target;

          if (priceContainer?.closest?.('.wishlist-hero-price-update')) {
            const moneyEl = priceContainer.closest('[data-wlh-item-price]') ||
                           priceContainer.closest('.money') ||
                           priceContainer.querySelector?.('[data-wlh-item-price]') ||
                           priceContainer.querySelector?.('.money');

            if (moneyEl) {
              const currentText = moneyEl.textContent.trim();
              // Check if the content was overwritten by Wishlist Hero
              if (!currentText.includes(this._options.pixLabel || 'no pix')) {
                shouldProcess = true;
              }
            }
          }
        }
      });

      if (shouldProcess) {
        // Debounce to avoid multiple rapid calls
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this.formatAll();
        }, 50);
      }
    });

    this._observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  },

  /**
   * Manually refresh all elements
   * @param {Object} options - Formatting options (optional, uses init options if not provided)
   */
  refresh(options) {
    if (options) {
      this._options = { ...this._options, ...options };
    }
    this._processedPrices = new WeakMap();
    this.formatAll();
  }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.WishlistHeroPriceFormatter.init());
} else {
  window.WishlistHeroPriceFormatter.init();
}
